import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
} from "@chakra-ui/react";
import { EdgeOverrideAmount } from "features/fund-family/setup/fund-structure/hooks/useAllocationRuleGraphForm";
import { useAllocationRuleGraphVersionBasisPreviewTableDataRetrieveSuspense } from "gen/endpoints/allocation-rule-graph-version/allocation-rule-graph-version";
import { GenericTableBadgeColorEnum } from "gen/model/genericTableBadgeColorEnum";
import { keys, pickBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import {
  OnChangeFn,
  RowSelectionState,
} from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { tryParseDecimal } from "shared/utils/DecimalUtils";

const OptOutsModal: React.FC<{
  isOpen: boolean;
  onModalClose: () => void;
  edgeOverrideAmounts: EdgeOverrideAmount[];
  onSaveOptOuts: (optOuts: EdgeOverrideAmount[]) => Promise<void>;
  allocationRuleGraphVersionId: string;
}> = ({
  isOpen,
  onModalClose,
  edgeOverrideAmounts,
  onSaveOptOuts,
  allocationRuleGraphVersionId,
}) => {
  const listOfOptOuts = useMemo(
    () =>
      edgeOverrideAmounts
        .filter(
          (override: EdgeOverrideAmount) =>
            tryParseDecimal(override.overrideAmount)?.isZero() &&
            !!override.securityOwnershipId,
        )
        .map((override: EdgeOverrideAmount) => ({
          security_ownership_id: override.securityOwnershipId,
        })),
    [edgeOverrideAmounts],
  );

  const { t } = useTranslation(["allocation"]);
  const { fundFamilyId } = useRequiredStringParams();
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const initialSelection = listOfOptOuts.reduce<Record<string, boolean>>(
      (acc, { security_ownership_id }) => {
        acc[security_ownership_id] = true;
        return acc;
      },
      {},
    );

    setRowSelection(initialSelection);
  }, [listOfOptOuts]);

  const onSave = async () => {
    await onSaveOptOuts(
      keys(pickBy(rowSelection)).map((id) => ({
        overrideAmount: "0",
        securityOwnershipId: id,
      })),
    );
  };

  const numberSelected = useMemo(
    () => keys(pickBy(rowSelection)).length,
    [rowSelection],
  );

  const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (rowSelection) => {
      setRowSelection(rowSelection);
    },
    [setRowSelection],
  );

  const { data: tableData } =
    useAllocationRuleGraphVersionBasisPreviewTableDataRetrieveSuspense(
      fundFamilyId,
      allocationRuleGraphVersionId,
    );

  return (
    <Modal
      isOpen={isOpen}
      size="full"
      scrollBehavior="inside"
      onClose={onModalClose}
    >
      <ModalContent backgroundColor="gray.100">
        <ModalBody background="white" display="flex" flexDirection="column">
          <TableBox
            isFullHeight={true}
            isDefaultExpanded={true}
            isFillHeight={true}
            isFullWidth={true}
          >
            <TableBoxHeader>
              <TableBoxTitle>{t("allocation:edit.optOut")}</TableBoxTitle>
              <TableBoxBadges>
                <Badge colorScheme={GenericTableBadgeColorEnum.warning}>
                  {t("allocation:ruleGraphEdit.optOutsSelected", {
                    count: numberSelected,
                  })}
                </Badge>
              </TableBoxBadges>
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable
                rowSelection={rowSelection}
                onRowSelectionChange={onRowSelectionChange}
                data={tableData}
                suppressStatusBar={true}
              />
            </TableBoxBody>
          </TableBox>
        </ModalBody>
        <ModalFooter
          borderTopWidth="1px"
          gap="8px"
          py="12px"
          px="24px"
          display="flex"
          columnGap="4"
          borderTop="1px"
          backgroundColor="white"
          borderTopColor="gray.200"
        >
          <Button
            colorScheme="secondary"
            onClick={() => {
              onModalClose();
            }}
          >
            {t("common:cancel")}
          </Button>
          <Button
            colorScheme="primary"
            onClick={async () => {
              await onSave();
              onModalClose();
            }}
          >
            {t("common:save_selections")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OptOutsModal;
